require("../icons/icons");

import svg4everybody from "svg4everybody";
import tippy from "tippy.js";
import datepicker from "js-datepicker";
import { Fancybox } from "@fancyapps/ui";
import Plyr from "plyr";

// CUSTOM MODULES
import Styleguide from "./modules/Styleguide";
import Header from "./modules/Header";
import Intro from "./modules/Intro";
import Tab from "./modules/Tab";
import Faq from "./modules/Faq";
import Diagonal from "./modules/Diagonal";
import ObjectHeader from "./modules/ObjectHeader";
import TextCarousel from "./modules/TextCarousel";
import Map from "./modules/Map";
import MapDetail from "./modules/MapDetail";
import MapOverview from "./modules/MapOverview";
import MapRadius from "./modules/MapRadius";
import Modal from "./modules/Modal";
import Residences from "./modules/Residences";
import FilterToggle from "./modules/FilterToggle";
import Toast from "./modules/Toast";
import SearchAssignmentForm from "./forms/SearchAssignmentForm";
import QuotationForm from "./forms/QuotationForm";

/**
 *  App class
 *
 *  The App class functions a the main back-bone of the javascript used in this
 *  project.
 *
 */

class App {
    /**
     *  Constructor
     */
    constructor() {
        document.addEventListener(
            "DOMContentLoaded",
            function () {
                this.onDocumentReady();
            }.bind(this)
        );
    }

    onDocumentReady() {
        svg4everybody();

        new Header();
        new Diagonal();

        this.replaceBr();

        tippy(".js-tooltip", {
            distance: "0.8rem",
            maxWidth: "28rem",
        });

        tippy(".js-tooltip-alt", {
            distance: "0.8rem",
            maxWidth: "28rem",
            theme: "alt",
        });

        const introElem = document.querySelector(".js-intro");
        if (introElem) {
            new Intro(introElem);
        }

        const faqElems = document.querySelectorAll(".js-faq-holder");
        for (let i = 0; i < faqElems.length; i++) {
            new Faq(faqElems[i]);
        }

        const dateInputs = document.querySelectorAll('input[type="date"]');
        for (let i = 0; i < dateInputs.length; i++) {
            const inputElem = dateInputs[i];

            datepicker(inputElem, {
                startDay: 1,
                customDays: ["Zo", "Ma", "Di", "Wo", "Do", "Vr", "Za"],
                customMonths: [
                    "januari",
                    "februari",
                    "maart",
                    "april",
                    "mei",
                    "juni",
                    "juli",
                    "augustus",
                    "september",
                    "oktober",
                    "november",
                    "december",
                ],
                customOverlayMonths: [
                    "jan",
                    "feb",
                    "mrt",
                    "apr",
                    "mei",
                    "jun",
                    "jul",
                    "aug",
                    "sep",
                    "okt",
                    "nov",
                    "dec",
                ],
                overlayButton: "Opslaan",
                overlayPlaceholder: "4-cijfer jaartal",
                disableMobile: true,
                formatter: (input, date, instance) => {
                    let month = "" + (date.getMonth() + 1);
                    let day = "" + date.getDate();
                    const year = date.getFullYear();

                    if (month.length < 2) {
                        month = "0" + month;
                    }
                    if (day.length < 2) {
                        day = "0" + day;
                    }

                    input.value = [year, month, day].join("-"); // => 'YYYY-MM-DD'
                },
            });
        }

        const mainElem = document.getElementsByTagName("MAIN")[0];
        if (mainElem) {
            const lastMainChild = mainElem.lastElementChild;
            if (lastMainChild) {
                if (lastMainChild.classList.contains("bg--subtle")) {
                    const footerElem = document.querySelector(".footer");
                    if (footerElem) {
                        footerElem.classList.add("bg--subtle");
                    }
                }
            }
        }

        Fancybox.bind(".js-fancybox-content, [data-fancybox]", {
            closeButton: "outside",
            buttons: ["close", "thumbs"],
        });

        const players = Array.from(document.querySelectorAll(".js-player")).map(
            (p) => new Plyr(p)
        );

        // Function to format price according to Dutch standards
        function formatPrice(price) {
            const formatter = new Intl.NumberFormat("nl-NL", {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
            return formatter.format(price);
        }

        // Function to calculate and update prices
        function calculatePrices() {
            const priceElements = document.querySelectorAll(".js-calculateprice");
            priceElements.forEach((element) => {
                if (element.dataset.processed === 'true') return;

                const basePrice = parseFloat(element.getAttribute("data-price"));
                const mutation = parseFloat(element.getAttribute("data-mutation"));
                const mutationType = element.getAttribute("data-mutation-type") || "percentage";

                let newPrice;
                if (mutationType === "fixed") {
                    newPrice = basePrice + mutation;
                } else {
                    newPrice = Math.round((basePrice * mutation) / 1000) * 1000;
                }

                element.textContent = formatPrice(newPrice);
                element.dataset.processed = 'true';
            });
        }

        // Initial price calculation
        calculatePrices();

        // Set up MutationObserver to watch for DOM changes
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.addedNodes.length) {
                    calculatePrices();
                }
            });
        });

        // Start observing the document with the configured parameters
        observer.observe(document.body, { 
            childList: true, 
            subtree: true 
        });
    }

    replaceBr() {
        const elems = document.querySelectorAll(".js-replace-br");
        if (elems.length) {
            for (let i = 0; i < elems.length; i++) {
                const elem = elems[i];
                let content = elem.innerHTML;

                content = content.replace(
                    /<br\s*\/?>/gi,
                    '<div class="custom-br"></div>'
                );
                elem.innerHTML = content;
            }
        }
    }

    handleStyleguide() {
        new Styleguide();
    }

    handleTab(elem) {
        new Tab(elem);
    }

    handleGrid(elem) {
        new Grid(elem);
    }

    handleMap(elem, locations) {
        new Map(elem, locations);
    }

    handleMapDetail(elem, locations) {
        new MapDetail(elem, locations);
    }

    handleMapOverview(elem, locations, baseUrl) {
        new MapOverview(elem, locations, baseUrl);
    }

    handleMapRadius(elem, locations) {
        new MapRadius(elem, locations);
    }

    handleObjectHeader(elem) {
        new ObjectHeader(elem);
    }

    handleTextCarousel(elem, strings) {
        new TextCarousel(elem, strings);
    }

    handleModal(triggers, modal) {
        new Modal(triggers, modal);
    }

    handleResidences(baseUrl) {
        new Residences(baseUrl);
    }
    handleFilterToggle() {
        new FilterToggle();
    }
    handleToast(container) {
        new Toast(container);
    }
    handleSearchAssignmentForm() {
        new SearchAssignmentForm();
    }
    handleQuotationForm() {
        new QuotationForm();
    }
}

window.app = new App();
